import React, { useEffect, useMemo, useState, useContext } from "react";
import Select from "react-select";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import "../styles/TutorProfileEdit.css";

const API_BASE = "https://tn.server.goatteachers.com/api";

const WEEKDAYS = [
  "Saturday",
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

export default function TutorProfileEdit() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Dropdown option lists
  const [options, setOptions] = useState({
    institutes: [],
    degrees: [],
    fields: [],
    mediums: [],
    levels: [],
    subjects: [],
    divisions: [],
    districts: [],
    areas: [],
  });

  // Form state
  const [form, setForm] = useState({
    // identity
    name: "",
    email: "",
    bio: "",
    gender: "",
    expected_remuneration: "",

    // education ids
    institute_id: "",
    degree_id: "",
    field_of_study_id: "",
    study_start_year: "",
    study_end_year: "",

    // preferences
    teaching_mode: "",
    availability: [], // ["Saturday", ...]
    medium_id: [], // [ids]
    level_id: [], // [ids]
    subject_id: [], // [ids]

    // location
    division_id: "",
    district_id: "",
    area_id: [], // [ids]

    // contact
    contact_phone: "",
    contact_whatsapp: "",
  });

  // Helpers to convert lists -> react-select options
  const toOptions = (arr) =>
    (arr || []).map((x) => ({ value: x.id, label: x.name }));
  const simpleOptions = (arr) =>
    (arr || []).map((x) => ({ value: x, label: x }));

  // Filtered dependent lists
  const filteredDistricts = useMemo(() => {
    if (!form.division_id) return options.districts;
    return options.districts.filter((d) => d.division_id === form.division_id);
  }, [options.districts, form.division_id]);

  const filteredAreas = useMemo(() => {
    if (!form.district_id) return options.areas;
    return options.areas.filter((a) => a.district_id === form.district_id);
  }, [options.areas, form.district_id]);

  // Load dropdowns + current profile
  useEffect(() => {
    let isMounted = true;

    async function loadAll() {
      try {
        const [inst, deg, fld, med, lev, sub, div, dis, ars] =
          await Promise.all([
            axios.get(`${API_BASE}/institutes`),
            axios.get(`${API_BASE}/degrees`),
            axios.get(`${API_BASE}/fields`),
            axios.get(`${API_BASE}/mediums`),
            axios.get(`${API_BASE}/levels`),
            axios.get(`${API_BASE}/subjects`),
            axios.get(`${API_BASE}/divisions`),
            axios.get(`${API_BASE}/districts`),
            axios.get(`${API_BASE}/areas`),
          ]);

        if (!isMounted) return;

        setOptions({
          institutes: inst.data || [],
          degrees: deg.data || [],
          fields: fld.data || [],
          mediums: med.data || [],
          levels: lev.data || [],
          subjects: sub.data || [],
          divisions: div.data || [],
          districts: dis.data || [],
          areas: ars.data || [],
        });

        // Load my profile (names + counts)
        const meRes = await axios.get(`${API_BASE}/tutors/me`, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });

        const me = meRes.data?.tutor || {};
        const tutorId = me.id;

        // Areas aren't in /me payload; fetch public profile for area ids
        let areaIds = [];
        if (tutorId) {
          try {
            const pub = await axios.get(`${API_BASE}/tutors/${tutorId}`);
            const areas = pub.data?.tutor?.areas || [];
            areaIds = areas.map((a) => a.id).filter(Boolean);
          } catch {
            // ignore
          }
        }

        // Map names → ids for mediums/levels/subjects
        const nameToId = (name, list) =>
          (list || []).find(
            (x) => (x.name || "").toLowerCase() === (name || "").toLowerCase(),
          )?.id;

        const medIds = (me.mediums || [])
          .map((nm) => nameToId(nm, med.data))
          .filter(Boolean);
        const levIds = (me.levels || [])
          .map((nm) => nameToId(nm, lev.data))
          .filter(Boolean);
        const subIds = (me.subjects || [])
          .map((nm) => nameToId(nm, sub.data))
          .filter(Boolean);

        // Populate the form
        setForm((f) => ({
          ...f,
          name: me.name || "",
          email: me.email || "",
          bio: me.bio || "",
          gender: me.gender || "",
          expected_remuneration: me.expected_remuneration || "",

          institute_id: nameToId(me.institute, inst.data) || "",
          degree_id: nameToId(me.degree, deg.data) || "",
          field_of_study_id: nameToId(me["field_of_study"], fld.data) || "",
          study_start_year: me.study_start_year || "",
          study_end_year: me.study_end_year || "",

          teaching_mode: me.teaching_mode || "",
          availability: Array.isArray(me.availability) ? me.availability : [],

          medium_id: medIds,
          level_id: levIds,
          subject_id: subIds,

          division_id: "", // leave blank to avoid filtering out areas
          district_id: "",
          area_id: areaIds,

          contact_phone: me.connect?.phone || "",
          contact_whatsapp: me.connect?.whatsapp || "",
        }));
      } catch (e) {
        setError("Failed to load profile.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadAll();
    return () => {
      isMounted = false;
    };
  }, [user?.token]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    // Build profile_subjects via cartesian of selected subject/level/medium ids
    const profile_subjects = [];
    (form.subject_id || []).forEach((sid) => {
      (form.level_id || []).forEach((lid) => {
        (form.medium_id || []).forEach((mid) => {
          profile_subjects.push({
            subject_id: Number(sid),
            level_id: Number(lid),
            medium_id: Number(mid),
          });
        });
      });
    });

    const payload = {
      name: form.name,
      email: form.email,
      bio: form.bio,
      gender: form.gender,
      expected_remuneration: form.expected_remuneration,

      institute_id: form.institute_id || null,
      degree_id: form.degree_id || null,
      field_of_study_id: form.field_of_study_id || null,
      study_start_year:
        form.study_start_year === "" ? null : Number(form.study_start_year),
      study_end_year:
        form.study_end_year === "" ? null : Number(form.study_end_year),

      teaching_mode: form.teaching_mode,
      availability: form.availability || [],

      areas: (form.area_id || []).map((x) => Number(x)),

      profile_subjects,

      contact_phone: form.contact_phone,
      contact_whatsapp: form.contact_whatsapp,
    };

    try {
      await axios.put(`${API_BASE}/tutors/me`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });
      navigate(-1);
    } catch (e) {
      setError(e?.response?.data?.message || "Update failed.");
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div className="tutor-profile-edit">
      <h2 style={{ marginBottom: 16 }}>Edit Tutor Profile</h2>
      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 8,
            background: "#ffe9e9",
            border: "1px solid #ffcdcd",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
        {/* Identity */}
        <section>
          <h3>Identity</h3>
          <div style={{ color: "black", display: "grid", gap: 12 }}>
            <label>
              <div>Name</div>
              <input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Full name"
                style={{ width: "100%", padding: 8 }}
              />
            </label>

            {/* <label>
              <div>Email</div>
              <input
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="Email"
                style={{ width: "100%", padding: 8 }}
              />
            </label>

            <label>
              <div>Gender</div>
              <Select
                placeholder="Select gender"
                value={
                  form.gender
                    ? { value: form.gender, label: form.gender }
                    : null
                }
                onChange={(sel) => setField("gender", sel ? sel.value : "")}
                options={simpleOptions(["Male", "Female"])}
              />
            </label> */}

            <label>
              <div>Bio</div>
              <textarea
                value={form.bio}
                onChange={(e) => setField("bio", e.target.value)}
                rows={4}
                style={{ width: "100%", padding: 8 }}
                placeholder="Short bio"
              />
            </label>
          </div>
        </section>

        {/* Education */}
        <section>
          <h3>Education</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <label>
              <div>Institute</div>
              <Select
                placeholder="Select institute"
                value={
                  toOptions(options.institutes).find(
                    (o) => o.value === form.institute_id,
                  ) || null
                }
                onChange={(sel) =>
                  setField("institute_id", sel ? sel.value : "")
                }
                options={toOptions(options.institutes)}
              />
            </label>

            <label>
              <div>Degree</div>
              <Select
                placeholder="Select degree"
                value={
                  toOptions(options.degrees).find(
                    (o) => o.value === form.degree_id,
                  ) || null
                }
                onChange={(sel) => setField("degree_id", sel ? sel.value : "")}
                options={toOptions(options.degrees)}
              />
            </label>

            <label>
              <div>Field of Study</div>
              <Select
                placeholder="Select field"
                value={
                  toOptions(options.fields).find(
                    (o) => o.value === form.field_of_study_id,
                  ) || null
                }
                onChange={(sel) =>
                  setField("field_of_study_id", sel ? sel.value : "")
                }
                options={toOptions(options.fields)}
              />
            </label>

            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "1fr 1fr",
              }}
            >
              {/* <label>
                <div>Study Start Year</div>
                <input
                  type="number"
                  value={form.study_start_year}
                  onChange={(e) => setField("study_start_year", e.target.value)}
                  placeholder="e.g., 2022"
                  style={{ width: "100%", padding: 8 }}
                />
              </label>
              <label>
                <div>Study End Year</div>
                <input
                  type="number"
                  value={form.study_end_year}
                  onChange={(e) => setField("study_end_year", e.target.value)}
                  placeholder="e.g., 2026 or leave blank"
                  style={{ width: "100%", padding: 8 }}
                />
              </label> */}
            </div>
          </div>
        </section>

        {/* Teaching Preferences */}
        <section>
          <h3>Teaching Preferences</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <label>
              <div>Teaching Mode</div>
              <Select
                placeholder="Select mode"
                value={
                  form.teaching_mode
                    ? { value: form.teaching_mode, label: form.teaching_mode }
                    : null
                }
                onChange={(sel) =>
                  setField("teaching_mode", sel ? sel.value : "")
                }
                options={simpleOptions(["Online", "In-person", "Both"])}
              />
            </label>

            <label>
              <div>Weekly Availability</div>
              <Select
                isMulti
                placeholder="Select days"
                value={simpleOptions(WEEKDAYS).filter((o) =>
                  (form.availability || []).includes(o.value),
                )}
                onChange={(sel) =>
                  setField(
                    "availability",
                    (sel || []).map((s) => s.value),
                  )
                }
                options={simpleOptions(WEEKDAYS)}
              />
            </label>

            <label>
              <div>Medium(s)</div>
              <Select
                isMulti
                placeholder="Select mediums"
                value={toOptions(options.mediums).filter((o) =>
                  (form.medium_id || []).includes(o.value),
                )}
                onChange={(sel) =>
                  setField(
                    "medium_id",
                    (sel || []).map((s) => s.value),
                  )
                }
                options={toOptions(options.mediums)}
              />
            </label>

            <label>
              <div>Level(s)</div>
              <Select
                isMulti
                placeholder="Select levels"
                value={toOptions(options.levels).filter((o) =>
                  (form.level_id || []).includes(o.value),
                )}
                onChange={(sel) =>
                  setField(
                    "level_id",
                    (sel || []).map((s) => s.value),
                  )
                }
                options={toOptions(options.levels)}
              />
            </label>

            <label>
              <div>Subject(s)</div>
              <Select
                isMulti
                placeholder="Select subjects"
                value={toOptions(options.subjects).filter((o) =>
                  (form.subject_id || []).includes(o.value),
                )}
                onChange={(sel) =>
                  setField(
                    "subject_id",
                    (sel || []).map((s) => s.value),
                  )
                }
                options={toOptions(options.subjects)}
              />
            </label>

            {/* <label>
              <div>Expected Remuneration</div>
              <input
                value={form.expected_remuneration}
                onChange={(e) =>
                  setField("expected_remuneration", e.target.value)
                }
                placeholder="e.g., 8000–12000 BDT/month"
                style={{ width: "100%", padding: 8 }}
              />
            </label> */}
          </div>
        </section>

        {/* Location */}
        <section>
          <h3>Location</h3>
          <div style={{ color: "black", display: "grid", gap: 12 }}>
            <label>
              <div>Division</div>
              <Select
                placeholder="Select division"
                value={
                  toOptions(options.divisions).find(
                    (o) => o.value === form.division_id,
                  ) || null
                }
                onChange={(sel) =>
                  setForm((f) => ({
                    ...f,
                    division_id: sel ? sel.value : "",
                    district_id: "",
                    // keep existing area_id as-is; user can re-pick if wants to filter
                  }))
                }
                options={toOptions(options.divisions)}
              />
            </label>

            <label>
              <div>District</div>
              <Select
                placeholder="Select district"
                value={
                  toOptions(filteredDistricts).find(
                    (o) => o.value === form.district_id,
                  ) || null
                }
                onChange={(sel) =>
                  setForm((f) => ({
                    ...f,
                    district_id: sel ? sel.value : "",
                  }))
                }
                options={toOptions(filteredDistricts)}
              />
            </label>

            <label>
              <div>Area(s)</div>
              <Select
                isMulti
                placeholder="Select multiple areas"
                value={
                  toOptions(options.areas).filter((o) =>
                    (form.area_id || []).includes(o.value),
                  )
                  // if a district is selected, show still-selected items even if filteredAreas hides them
                }
                onChange={(sel) =>
                  setField(
                    "area_id",
                    (sel || []).map((s) => s.value),
                  )
                }
                options={toOptions(filteredAreas)}
              />
            </label>
          </div>
        </section>

        {/* Contact */}
        <section>
          <h3>Contact</h3>
          <div style={{ color: "black", display: "grid", gap: 12 }}>
            <label>
              <div>Phone</div>
              <input
                value={form.contact_phone}
                onChange={(e) =>
                  setField(
                    "contact_phone",
                    e.target.value.replace(/\D/g, "").slice(0, 11),
                  )
                }
                placeholder="11-digit phone number"
                style={{ width: "100%", padding: 8 }}
              />
            </label>

            <label>
              <div>WhatsApp</div>
              <input
                value={form.contact_whatsapp}
                onChange={(e) => setField("contact_whatsapp", e.target.value)}
                placeholder="WhatsApp number"
                style={{ width: "100%", padding: 8 }}
              />
            </label>
          </div>
        </section>

        <div className="tpe-actions">
          <button
            type="button"
            className="tpe-btn tpe-btn-ghost"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={saving}
            className="tpe-btn tpe-btn-primary"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
